"use client";
import React, { useRef, useState, useEffect } from "react";
import VisualEffectsPanel from "./VisualEffectsPanel";
import VideoPreviewArea from "./VideoPreviewArea";
import ActionsSidebar from "./ActionsSidebar";
import { Container } from "@/components/ui/container";
import { Stack } from "@/components/ui/layout";
import { Heading } from "@/components/ui/typography";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

function StartVideo() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideoURL, setRecordedVideoURL] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showRecordingInstruction, setShowRecordingInstruction] = useState(false);

  useEffect(() => {
    if (isCameraOn && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isCameraOn]);

  const startCamera = async () => {
    setShowInstructions(false);
    setShowRecordingInstruction(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setIsCameraOn(true);
      setRecordedVideoURL(null); // Reset recorded video
    } catch (error) {
      console.error("Error accessing camera:", error);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraOn(false);
  };

  const startRecording = () => {
    setShowRecordingInstruction(false);
    if (streamRef.current) {
      const options = { mimeType: "video/webm; codecs=vp8" }; // Fix for some browsers
      const mediaRecorder = new MediaRecorder(streamRef.current, options);
      mediaRecorderRef.current = mediaRecorder;
      let chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (chunks.length > 0) {
          const blob = new Blob(chunks, { type: "video/webm" });
          console.log("Blob created, size:", blob.size);

          if (blob.size > 0) {
            const url = URL.createObjectURL(blob);
            console.log("Generated video URL:", url);

            setRecordedVideoURL(prev => {
              if (prev) URL.revokeObjectURL(prev); // Revoke old URL
              return url;
            });

            stopCamera();
          } else {
            console.error("Blob is empty! Recording failed.");
          }
        } else {
          console.error("No data received! Recording failed.");
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      setTimeout(() => {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }, 500); // Ensure some data is recorded
    }
  };

  const startOver = () => {
    setRecordedVideoURL(null);
    setIsCameraOn(false);
    startCamera();
  };

  return (
    <section className={cn(
      "min-h-screen w-full",
      "bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e]",
      "p-8 font-['Poppins',sans-serif]"
    )}>
      <Container className="max-w-7xl">
        <Heading level="h1" className="text-3xl font-bold text-white text-center mb-8">
          Create Video
        </Heading>

        <Card className={cn(
          "bg-white/5 backdrop-blur-md",
          "border border-white/10 rounded-2xl p-6",
          "shadow-xl"
        )}>
          <CardContent className="p-0">
            <div className={cn("flex gap-6")}>
              <div className={cn("w-[22%]")}>
                <VisualEffectsPanel />
              </div>

              <div className={cn("flex-1 flex flex-col items-center justify-center gap-6")}>
                {showInstructions && (
                  <div className={cn("text-white text-lg font-medium animate-pulse")}>
                    Click the play button to start
                  </div>
                )}

                {showRecordingInstruction && (
                  <div className={cn("text-white text-lg font-medium animate-pulse")}>
                    Click the record button to start recording
                  </div>
                )}
                <VideoPreviewArea
                  videoRef={videoRef}
                  isCameraOn={isCameraOn}
                  isRecording={isRecording}
                  recordedVideoURL={recordedVideoURL}
                  startCamera={startCamera}
                  stopCamera={stopCamera}
                  startRecording={startRecording}
                  stopRecording={stopRecording}
                  startOver={startOver}
                />
                
                <Card className={cn(
                  "w-full bg-gradient-to-r from-[#5D52D9] to-[#0A65CC]",
                  "text-white border-0 shadow-lg animate-pulse"
                )}>
                  <CardContent className="p-4">
                    <Heading level="h3" className="text-lg font-semibold mb-3 flex items-center gap-2">
                      💡 Recording Instructions
                    </Heading>
                    <ul className={cn("space-y-2 text-sm list-disc list-inside")}>
                      <li>Ensure good lighting and a clean background</li>
                      <li>Speak clearly and at a moderate pace</li>
                      <li>Your introduction should include:
                        <ul className="ml-4 mt-1 space-y-1">
                          <li>Your name and background</li>
                          <li>Your skills and experience</li>
                          <li>Why you're interested in this opportunity</li>
                        </ul>
                      </li>
                      <li>Minimum recording length: 10 seconds</li>
                      <li>Click the record button to begin after countdown</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div className={cn("w-[22%]")}>
                <ActionsSidebar />
              </div>
            </div>
          </CardContent>
        </Card>
      </Container>
    </section>
  );
}

export default StartVideo;